import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Table, InputNumber, Button, Space, Form } from 'antd';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import DynamicSelect from '../../../components/Select/DynamicSelect';
import { putData } from '../../../actions/developer';
import { useDispatch } from 'react-redux';
import { getData } from '../../../actions/apiCall';

interface SkillRow {
    key: string;
    skill?: string;
    experienceFrom?: number;
    experienceTo?: number;
    budgetFrom?: number;
    budgetTo?: number;
}

interface Props {
    onLoadingChange?: (loading: boolean) => void;
}

const SkillRateCardTable = forwardRef((props: Props, ref) => {
    const { onLoadingChange } = props;
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState<SkillRow[]>([{ key: '0' }]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [isDisabled, setDisabled] = React.useState<any>(false);
    const submitPromiseRef = React.useRef<{ resolve?: (value: any) => void; reject?: (reason?: any) => void }>({});

    const [vendorId, setVendorId] = React.useState<string>('');
    const dispatch = useDispatch();

    const getQueryStep = () => {
        const params = new URLSearchParams(location.search);
        const _vendorId = params.get('vendor')
        setVendorId(_vendorId || '')
        return _vendorId
    };

    useEffect(() => {
        getQueryStep()
    }, [location.search]);

    useEffect(() => {
        if (vendorId) {
            getDataSource()
        }
    }, [vendorId]);

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useImperativeHandle(ref, () => ({
        submitStep: () => {
            return new Promise((resolve, reject) => {
                submitPromiseRef.current = { resolve, reject };
                form.submit();
            });
        }
    }));

    const handleAdd = () => {
        const newKey = `${Date.now()}`;
        setDataSource([...dataSource, { key: newKey }]);
    };

    const handleRemove = (key: string) => {
        setDataSource(dataSource.filter(item => item.key !== key));
    };

    const columns = [
        {
            title: 'Skills',
            dataIndex: 'skill',
            render: (_: any, record: SkillRow, index: number) => (
                <Space>
                    <Form.Item
                        name={['rows', index, 'skill']}
                        rules={[{ required: true, message: 'Please select a skill' }]}
                        initialValue={record.skill}
                    >
                        <DynamicSelect
                            keyName="skillName"
                            addNew={true}
                            rest={{ style: { width: 150 } }}
                        />
                    </Form.Item>
                </Space>
            ),
        },
        {
            title: 'Experience (Years)',
            dataIndex: 'experience',
            render: (_: any, record: SkillRow, index: number) => (
                <Space>
                    <Form.Item
                        name={['rows', index, 'experienceFrom']}
                        rules={[{ required: true, message: 'Required' }]}
                        initialValue={record.experienceFrom}
                    >
                        <InputNumber placeholder="From" min={0} maxLength={2} />
                    </Form.Item>
                    <Form.Item
                        name={['rows', index, 'experienceTo']}
                        rules={[{ required: true, message: 'Required' }]}
                        initialValue={record.experienceTo}
                    >
                        <InputNumber placeholder="To" min={0} maxLength={2} />
                    </Form.Item>
                </Space>
            ),
        },
        {
            title: 'Budget (₹)',
            dataIndex: 'budget',
            render: (_: any, record: SkillRow, index: number) => (
                <Space>
                    <Form.Item
                        name={['rows', index, 'budgetFrom']}
                        rules={[{ required: true, message: 'Required' }, {
                            pattern: /^[0-9]{5,}$/,
                            message: 'Enter a valid one',
                        }]}
                        initialValue={record.budgetFrom}
                    >
                        <InputNumber placeholder="From" min={0} suffix={'PM'} />
                    </Form.Item>
                    <Form.Item
                        name={['rows', index, 'budgetTo']}
                        rules={[{ required: true, message: 'Required' }, {
                            pattern: /^[0-9]{5,}$/,
                            message: 'Enter a valid one',
                        }]}
                        initialValue={record.budgetTo}
                    >
                        <InputNumber placeholder="To" min={0} suffix={'PM'} />
                    </Form.Item>
                </Space>
            ),
        },
        {
            title: '',
            dataIndex: 'actions',
            render: (_: any, record: SkillRow, index) => (
                <Space>
                    {dataSource.length > 1 && (
                        <Button
                            type="link"
                            icon={<MinusCircleOutlined />}
                            danger
                            disabled={isDisabled}
                            onClick={() => handleRemove(record.key)}
                        />
                    )}
                    {dataSource.length == index + 1 && <Button
                        type="link"
                        disabled={isDisabled}
                        icon={<PlusCircleOutlined />}
                        onClick={handleAdd}
                    />}
                </Space>
            ),
        },
    ];


    const getDataSource = async () => {
        const resData = await dispatch(getData(`/v1/vendor/${vendorId}`));
        if (resData?.data?.skills?.length > 0) {
            const rows = resData?.data.skills.map((item) => ({ ...item }));
            setTimeout(() => {
                form.setFieldsValue({ rows });
                setDataSource(rows.map((item, index) => ({ ...item, key: `${index}` })));
                setDisabled(resData?.data?.msaSigned || resData?.data?.ndaSigned);
            }, 0);
        } else {
            form.setFieldsValue({ rows: [{ key: `0` }] });
            setDataSource([{ key: `0` }]);
        }
    };

    const onFinish = async (values: any) => {
        setLoading(true);
        try {
            const resData = await dispatch(putData(`/v1/vendor/${vendorId}`, { skills: values.rows }));
            if (resData) {
                submitPromiseRef.current?.resolve?.(values.rows);
            } else {
                submitPromiseRef.current?.reject?.(null);
            }
        } catch (error) {
            setLoading(false);
            submitPromiseRef.current?.reject?.(error);
        }

        setTimeout(() => {
            setLoading(false);
            submitPromiseRef.current?.resolve?.(values.rows);
        }, 1000);

    };

    return (
        <Form form={form}
            onFinish={onFinish}
            onFinishFailed={() => {
                setLoading(false);
                submitPromiseRef.current?.reject?.();
            }}
            disabled={isDisabled}
            initialValues={{ rows: dataSource }}>
            <Table
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                rowKey="key"
            />
        </Form>
    );
})

export default SkillRateCardTable;


// import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
// import { Form, InputNumber, Button, Space, Typography, Divider } from 'antd';
// import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
// import DynamicSelect from '../../../components/Select/DynamicSelect';
// import { useDispatch } from 'react-redux';
// import { getData } from '../../../actions/apiCall';
// import { putData } from '../../../actions/developer';

// const { Title } = Typography;

// interface SkillRow {
//     key: string;
//     skill?: string;
//     experienceFrom?: number;
//     experienceTo?: number;
//     budgetFrom?: number;
//     budgetTo?: number;
// }

// interface Props {
//     onLoadingChange?: (loading: boolean) => void;
// }

// const SkillRateCardFormList = forwardRef((props: Props, ref) => {
//     const { onLoadingChange } = props;
//     const [form] = Form.useForm();
//     const dispatch = useDispatch();
//     const [loading, setLoading] = useState(false);
//     const [vendorId, setVendorId] = useState('');
//     const submitPromiseRef = React.useRef<{ resolve?: (value: any) => void; reject?: (reason?: any) => void }>({});

//     const getQueryStep = () => {
//         const params = new URLSearchParams(location.search);
//         const _vendorId = params.get('vendor');
//         setVendorId(_vendorId || '');
//         return _vendorId;
//     };

//     useEffect(() => {
//         getQueryStep();
//     }, [location.search]);

//     useEffect(() => {
//         if (vendorId) {
//             fetchData();
//         }
//     }, [vendorId]);

//     useEffect(() => {
//         onLoadingChange?.(loading);
//     }, [loading]);

//     useImperativeHandle(ref, () => ({
//         submitStep: () => {
//             return new Promise((resolve, reject) => {
//                 submitPromiseRef.current = { resolve, reject };
//                 form.submit();
//             });
//         },
//     }));

//     const fetchData = async () => {
//         const resData = await dispatch(getData(`/v1/vendor/${vendorId}`));
//         console.log('resData', resData);
//         if (resData?.data.skills?.length > 0) {
//             const rows = resData.data.skills.map((item) => ({ ...item }));
//             setTimeout(() => {
//                 form.setFieldsValue({ rows });
//             }, 0);
//         } else {
//             form.setFieldsValue({ rows: [{}] });
//         }
//     };

//     const onFinish = async (values: any) => {
//         setLoading(true);
//         try {
//             const res = await dispatch(putData(`/v1/vendor/${vendorId}`, { skills: values.rows }));
//             if (res) {
//                 submitPromiseRef.current?.resolve?.(values.rows);
//             } else {
//                 submitPromiseRef.current?.reject?.();
//             }
//         } catch (err) {
//             submitPromiseRef.current?.reject?.(err);
//         } finally {
//             setLoading(false);
//         }
//     };

//     return (
//         <Form
//             form={form}
//             onFinish={onFinish}
//             onFinishFailed={() => submitPromiseRef.current?.reject?.()}
//             initialValues={{ rows: [{}] }}
//             layout="vertical"
//         >
//             <Title level={5}>Skill Rate Card</Title>
//             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 80px', gap: 16, paddingBottom: 8 }}>
//                 <div><b>Skill</b></div>
//                 <div><b>Experience (From - To)</b></div>
//                 <div><b>Budget (From - To)</b></div>
//                 <div></div>
//             </div>

//             <Form.List name="rows">
//                 {(fields, { add, remove }) => (
//                     <>
//                         {fields.map(({ key, name }, index) => (
//                             <div
//                                 key={key}
//                                 style={{
//                                     display: 'grid',
//                                     gridTemplateColumns: '1fr 1fr 1fr 1fr 80px',
//                                     gap: 16,
//                                     alignItems: 'center',
//                                     marginBottom: 12,
//                                 }}
//                             >
//                                 <Form.Item
//                                     name={[name, 'skill']}
//                                     rules={[{ required: true, message: 'Select skill' }]}
//                                 >
//                                     <DynamicSelect
//                                         keyName="skillName"
//                                         addNew
//                                         rest={{ style: { width: '100%' } }}
//                                     />
//                                 </Form.Item>

//                                 <Space>
//                                     <Form.Item
//                                         name={[name, 'experienceFrom']}
//                                         rules={[{ required: true, message: 'Required' }]}
//                                     >
//                                         <InputNumber placeholder="From" min={0} />
//                                     </Form.Item>
//                                     <Form.Item
//                                         name={[name, 'experienceTo']}
//                                         rules={[{ required: true, message: 'Required' }]}
//                                     >
//                                         <InputNumber placeholder="To" min={0} />
//                                     </Form.Item>
//                                 </Space>

//                                 <Space>
//                                     <Form.Item
//                                         name={[name, 'budgetFrom']}
//                                         rules={[
//                                             { required: true, message: 'Required' },
//                                             {
//                                                 pattern: /^[0-9]{5,}$/,
//                                                 message: 'Enter a valid budget',
//                                             },
//                                         ]}
//                                     >
//                                         <InputNumber placeholder="From" min={0} />
//                                     </Form.Item>
//                                     <Form.Item
//                                         name={[name, 'budgetTo']}
//                                         rules={[
//                                             { required: true, message: 'Required' },
//                                             {
//                                                 pattern: /^[0-9]{5,}$/,
//                                                 message: 'Enter a valid budget',
//                                             },
//                                         ]}
//                                     >
//                                         <InputNumber placeholder="To" min={0} />
//                                     </Form.Item>
//                                 </Space>

//                                 <div>
//                                     {fields.length > 1 && (
//                                         <Button
//                                             type="link"
//                                             icon={<MinusCircleOutlined />}
//                                             danger
//                                             onClick={() => remove(name)}
//                                         />
//                                     )}
//                                     {index === fields.length - 1 && (
//                                         <Button
//                                             type="link"
//                                             icon={<PlusCircleOutlined />}
//                                             onClick={() => add()}
//                                         />
//                                     )}
//                                 </div>
//                             </div>
//                         ))}
//                     </>
//                 )}
//             </Form.List>
//         </Form>
//     );
// });

// export default SkillRateCardFormList;
